import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import SelectAddress from './popups/SelectAddress';
import { ModalVariant, PopUpState, RequiredAddressFields } from './constants/constants';
import { API_STATUS } from 'core-lib/constants/network';
import MapAddress from './popups/MapAddress';
import { UserAddress } from './types/types';
import { ModalTypeTypes } from 'ui-v2/components/Modal/Modal';
import { handleBack } from 'core-lib/utils/common';
import { useAddress } from '../../hooks/useAddress';
import { Source } from 'core-lib/constants/sourceSKU';
import MaterialSpinner from '../MaterialSpinner';
import OverlayLoader from '../OverlayLoader';
import { semanticColors } from 'ui-v2/tokens/colors';

type AddAddressV3Props = {
  isShow?: boolean;
  onClose?: () => void;
  variant?: ModalTypeTypes;
  selectedAddress?: UserAddress;
  setSelectedAddress?: (arg: any) => void;
  checkServiceable?: boolean;
  serviceName?: string;
  showListPostAddition?: boolean;
  zIndex?: number;
  withProceedBtn?: boolean;
  blurredBg?: boolean;
};

export const AddAddressV3 = ({
  isShow = true,
  onClose = () => {},
  variant = ModalVariant.BottomSheet,
  selectedAddress,
  setSelectedAddress,
  checkServiceable = false,
  serviceName = Source.ParkplusStore,
  showListPostAddition = false,
  zIndex = 105,
  withProceedBtn = false,
  blurredBg = false,
}: AddAddressV3Props) => {
  const [popUpState, setPopUpState] = useState<string[]>([]);
  const [editAddress, setEditAddress] = useState(null);

  const showPopUp = popUpState[popUpState.length - 1];
  const { isAuthenticated } = useSelector(state => state.auth);
  const { addressList, apiStatus } = useSelector((state: any) => state.auth.userAddresses);

  // fetch all addresses
  const { fetchUserAddresses } = useAddress();

  // push popup name to PopUp stack
  const showNewPopUp = (newPopUp: string) => setPopUpState([...popUpState, newPopUp]);

  // remove the last element from PopUp stack to show previous opened PopUp
  const hidePopUp = () => setPopUpState([...popUpState.slice(0, -1)]);

  const handleClose = () => {
    if (variant === ModalVariant.Page) {
      handleBack();
    } else {
      setPopUpState([]);
      onClose();
    }
  };

  const handleUseAddress = add => {
    if (variant === ModalVariant.BottomSheet) {
      setSelectedAddress(add);
      setPopUpState([]);
      onClose();
    }
  };

  const onAddressAdditionRedirect = () => {
    if (editAddress || variant === ModalVariant.Page || showListPostAddition) {
      if (isAuthenticated) {
        fetchUserAddresses();
      }
      showNewPopUp(PopUpState.SelectAddress);
    } else {
      setPopUpState([]);
      onClose();
      if (isAuthenticated) {
        fetchUserAddresses();
      }
    }
    if (editAddress) {
      setEditAddress(null);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (addressList.length > 0 || (variant === ModalVariant.Page && !addressList.length)) {
        showNewPopUp(PopUpState.SelectAddress);
      } else if (apiStatus === API_STATUS.success) {
        showNewPopUp(PopUpState.MapAddress);
      } else if (apiStatus === API_STATUS.failure) {
        onClose();
      }
    } else {
      showNewPopUp(PopUpState.MapAddress);
    }
  }, [addressList, apiStatus]);

  useEffect(() => {
    if (isAuthenticated && (apiStatus === API_STATUS.default || apiStatus === API_STATUS.failure)) {
      fetchUserAddresses();
    }
  }, []);

  if (isAuthenticated && [API_STATUS.default, API_STATUS.in_progress].includes(apiStatus)) {
    return (
      <OverlayLoader
        loader={<MaterialSpinner width={60} height={60} />}
        styleProp={{
          background:
            variant === ModalVariant.Page ? semanticColors.bg.overlay : 'rgba(0, 0, 0, 0.3)',
        }}
      />
    );
  }

  return (
    <div>
      {
        {
          [PopUpState.SelectAddress]: (
            <SelectAddress
              isShow
              variant={variant}
              onClose={handleClose}
              onEditAddress={address => {
                setEditAddress(address);
                showNewPopUp(PopUpState.MapAddress);
              }}
              userSelectedAddress={selectedAddress}
              onUseAddress={handleUseAddress}
              onNewAddress={() => {
                showNewPopUp(PopUpState.MapAddress);
              }}
              checkServiceable={checkServiceable}
              serviceName={serviceName}
              zIndex={zIndex}
              withProceedBtn={withProceedBtn}
              blurredBg={blurredBg}
            />
          ),

          [PopUpState.MapAddress]: (
            <MapAddress
              onClose={() => {
                hidePopUp();
                if (!addressList.length) {
                  onClose();
                }
                if (editAddress) {
                  setEditAddress(null);
                }
              }}
              variant={variant}
              onDone={onAddressAdditionRedirect}
              addressValidation={RequiredAddressFields}
              isEditAddress={!!editAddress}
              editAddress={editAddress}
              checkServiceable={checkServiceable}
              serviceName={serviceName}
              zIndex={zIndex}
            />
          ),
        }[showPopUp]
      }
    </div>
  );
};
